import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type TimeOption = {
  label: string;
  value: number;
};

type TimeColumnType = 'hour' | 'minute';

@Component({
  standalone: false,
  selector: 'admin-time-picker',
  templateUrl: './admin-time-picker.component.html',
  styleUrls: ['./admin-time-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminTimePickerComponent),
      multi: true,
    },
  ],
})
export class AdminTimePickerComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select time';
  @Input() allowClear = true;

  value: Date | null = null;
  draftValue: Date = this.roundToMinute(new Date());
  isOpen = false;
  disabled = false;
  readonly hourOptions: TimeOption[] = Array.from({ length: 24 }, (_, value) => ({
    label: this.pad(value),
    value,
  }));
  readonly minuteOptions: TimeOption[] = Array.from({ length: 60 }, (_, value) => ({
    label: this.pad(value),
    value,
  }));

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  writeValue(value: Date | string | null | undefined): void {
    if (value == null || value === '') {
      this.value = null;
      this.draftValue = this.roundToMinute(new Date());
      return;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    this.value = Number.isNaN(parsed.getTime()) ? null : parsed;
    this.draftValue = this.value ? new Date(this.value) : this.roundToMinute(new Date());
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openPicker(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.disabled) return;
    this.draftValue = this.value
      ? new Date(this.value)
      : this.roundToMinute(new Date());
    this.isOpen = true;
    window.setTimeout(() => this.scrollSelectedTimeIntoView());
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.setValue(null);
    this.isOpen = false;
  }

  close(): void {
    this.isOpen = false;
    this.onTouched();
  }

  setHour(value: string | number, shouldCenter = true): void {
    const hour = Number(value);
    if (Number.isNaN(hour)) return;
    const next = new Date(this.draftValue);
    next.setHours(this.clamp(hour, 0, 23));
    this.draftValue = next;
    this.commitDraft();
    if (shouldCenter) {
      window.setTimeout(() => this.scrollSelectedTimeIntoView());
    }
  }

  setMinute(value: string | number, shouldCenter = true): void {
    const minute = Number(value);
    if (Number.isNaN(minute)) return;
    const next = new Date(this.draftValue);
    next.setMinutes(this.clamp(minute, 0, 59));
    this.draftValue = next;
    this.commitDraft();
    if (shouldCenter) {
      window.setTimeout(() => this.scrollSelectedTimeIntoView());
    }
  }

  onTimeColumnScroll(event: Event, type: TimeColumnType): void {
    const container = event.target as HTMLElement;
    const nextValue = this.findCenteredTimeValue(container);
    if (nextValue == null) return;

    if (type === 'hour' && nextValue !== this.draftValue.getHours()) {
      this.setHour(nextValue, false);
    }

    if (type === 'minute' && nextValue !== this.draftValue.getMinutes()) {
      this.setMinute(nextValue, false);
    }
  }

  get displayValue(): string {
    return this.value ? this.formatTime(this.value) : '';
  }

  private setValue(value: Date | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  private commitDraft(): void {
    this.setValue(new Date(this.draftValue));
  }

  private formatTime(date: Date): string {
    return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
  }

  private roundToMinute(date: Date): Date {
    const next = new Date(date);
    next.setSeconds(0, 0);
    return next;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private findCenteredTimeValue(container: HTMLElement): number | null {
    const options = Array.from(
      container.querySelectorAll<HTMLElement>('.time-scroll-option'),
    );
    if (options.length === 0) return null;

    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    let closestOption: HTMLElement | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const option of options) {
      const optionRect = option.getBoundingClientRect();
      const optionCenterY = optionRect.top + optionRect.height / 2;
      const distance = Math.abs(optionCenterY - centerY);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestOption = option;
      }
    }

    const rawValue = closestOption?.dataset['value'];
    if (rawValue == null) return null;

    const value = Number(rawValue);
    return Number.isNaN(value) ? null : value;
  }

  private scrollSelectedTimeIntoView(): void {
    const selectedOptions = this.elementRef.nativeElement.querySelectorAll(
      '.time-scroll-option.selected',
    );
    selectedOptions.forEach((option) => {
      option.scrollIntoView({ block: 'center' });
    });
  }
}
